import type { SeverityLevel } from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import { Console } from "./console";
import type { CustomError } from "./custom.error";
import type { HttpError } from "./http.error";

const EXCLUDED_STACKS = [
	"uc.js", // CookieBot
];

const EXCLUDED_MESSAGES = [
	/Failed to fetch/gm,
	/Load failed/gm,
	/Az összes havi hiteltörlesztésed mértéke túl magas a jövedelmedhez képest/gm,
	/windowOnloadTriggered/gm,
	/self.crypto.randomUUID/gm,
];

export function sentryInit(dsn: string, release: string, environment: string): void {
	Sentry.init({
		dsn,
		release,
		environment,
		sendDefaultPii: true,
		integrations: [
			Sentry.browserTracingIntegration({
				enableInp: true,
			}),
			Sentry.replayIntegration(),
		],
		beforeSend(event, hint) {
			if (hint?.originalException) {
				const error = hint.originalException as Error;
				const stack = error?.stack;
				const message = error?.message;

				// Ignore errors by stack
				if (
					typeof stack === "string" &&
					EXCLUDED_STACKS.some((excludedStack) => stack.includes(excludedStack))
				) {
					return null;
				}

				// Ignore errors by message
				if (
					typeof message === "string" &&
					EXCLUDED_MESSAGES.some((excludedMessageRegex) => excludedMessageRegex.test(message))
				) {
					return null;
				}
			}

			return event;
		},
		tracesSampleRate: 0.01,
		sampleRate: 0.15,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0.125,
	});
}

export function logCustomError(message: string, extras: Record<string, any>, level: SeverityLevel = "warning"): void {
	Sentry.captureMessage(message, (scope) => {
		scope.setLevel(level);
		scope.setExtras(extras);

		return scope;
	});

	Console.error(message, extras);
}

export function logError(error: Error | CustomError): void {
	Console.error(error);

	if (
		(error.name === "HttpError" && [0, 401, 403, 404, 410].includes((error as HttpError).status)) ||
		("needLog" in error && error.needLog === false)
	) {
		return;
	}

	Sentry.captureException(error, (scope) => {
		if ("getExtras" in error) {
			scope.setExtras(error.getExtras());
		}

		return scope;
	});
}
